/**
 * iter86.21 — Tournament format helpers.
 *
 * Season (round-robin) formats — Premier League, La Liga, Serie A,
 * Super Draft, and any future Super Cup / European Club Season templates
 * — have NO bracket. That changes the UX in three specific places:
 *
 *   1. Champion Bonus Pick (a.k.a. knockout_pick_team_id) is meaningless
 *      because there is no stage-decay schedule to price it against.
 *   2. Third-place playoff is meaningless (no semifinals).
 *   3. Power Pick still applies, but the multiplier hits the ENTIRE
 *      season's W/D/L for the picked team (not just KO rounds), and it
 *      locks the moment the first fixture kicks off.
 *
 * Keep the check narrow and centralised so future formats
 * (double-round-robin, group-only with playoffs, etc.) can slot in
 * without hunting for every UI branch. Backend mirror:
 * `power_pick._is_season_format` in /app/backend/power_pick.py.
 */
export function isSeasonFormat(tournament) {
    if (!tournament) return false;
    return (tournament.format || "").toLowerCase() === "round_robin";
}

/**
 * iter86.31 — Golf formats (Slash Golf templates). No matches, no
 * bracket, no schedule: scoring comes from live golfer strokes.
 * Pick'em variant additionally swaps the drafted-roster flow for
 * tiered-pot picks and renders average-strokes leaderboards.
 */
export function isGolfFormat(tournament) {
    if (!tournament) return false;
    return (tournament.format || "").toLowerCase() === "golf";
}

export function isGolfPickem(tournament) {
    return isGolfFormat(tournament) && (tournament.golf?.mode === "pickem_lowest_avg"
        || tournament.template === "golf_pickem_lowest_avg_api");
}

/**
 * iter86.77.18 — A participant's picks for a SPECIFIC tournament.
 * The flat `assigned_team_ids` mirrors the ACTIVE tournament only, so
 * seeding a pick sheet from it leaks foreign team ids when viewing a
 * non-active tournament in a multi-tournament league (production bug:
 * "Unknown club in selection"). Always prefer the per-tournament map
 * and sanitize against the tournament's own team ids.
 */
export function myPicksForTournament(me, tournament) {
    if (!me || !tournament) return [];
    const byT = me.assigned_team_ids_by_tournament;
    const raw = (byT && typeof byT === "object" && Object.keys(byT).length > 0)
        ? (byT[tournament.id] || [])
        : (me.assigned_team_ids || []);
    const validIds = new Set((tournament.teams || []).map(t => t.id));
    return raw.filter(id => validIds.has(id));
}

/**
 * iter87.278.2 — team_ids the player has already been notified about
 * dropping out of the real playoff field post-lock (backend-flagged,
 * see `season_pickem.reconcile_dropped_pot_picks`). Persists until
 * they swap in a replacement via `replaceDroppedPotPick`.
 */
export function myDroppedPicksForTournament(me, tournament) {
    if (!me || !tournament) return [];
    return ((me.dropped_pot_picks_by_tournament || {})[tournament.id]) || [];
}
export function isPickBasedFormat(tournament) {
    if (!tournament) return false;
    return isGolfFormat(tournament) || tournament.pickem?.mode === "pot";
}

/** Picks required + unit wording for pick-based templates. */
export function potPickInfo(tournament) {
    if (isGolfFormat(tournament)) {
        const n = (tournament?.golf?.pot_rules || []).reduce((s, r) => s + (r.picks || 0), 0);
        return { picksRequired: n || 8, unit: "golfers" };
    }
    const n = (tournament?.pickem?.pot_rules || []).reduce((s, r) => s + (r.picks || 0), 0);
    return {
        picksRequired: n || 5,
        unit: tournament?.template === "nfl_season_api" ? "teams" : "clubs",
    };
}
